import { create } from 'zustand'
import { User as SupabaseUser } from '@supabase/supabase-js'
import { User, UserRole } from '../types'

interface AuthState {
  user: User | null
  supabaseUser: SupabaseUser | null
  loading: boolean
  setUser: (user: User | null) => void
  setSupabaseUser: (user: SupabaseUser | null) => void
  setLoading: (loading: boolean) => void
  refreshUser: () => Promise<void>
  logout: () => void
  isCoach: () => boolean
  isCoachee: () => boolean
  isAdmin: () => boolean
  initializeWithTimeout: (timeoutMs?: number) => Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  supabaseUser: null,
  loading: true,
  setUser: (user) => set({ user }),
  setSupabaseUser: (supabaseUser) => set({ supabaseUser }),
  setLoading: (loading) => set({ loading }),
  refreshUser: async () => {
    const { supabaseUser } = get()
    if (supabaseUser) {
      // Import here to avoid circular dependency
      const UserService = (await import('../services/userService')).UserService
      const profile = await UserService.getOrCreateProfile(supabaseUser)
      if (profile) {
        set({ user: profile })
      }
    }
  },
  logout: () => set({ user: null, supabaseUser: null, loading: false }),
  isCoach: () => get().user?.role === 'coach',
  isCoachee: () => get().user?.role === 'coachee',
  isAdmin: () => get().user?.role === 'admin',
  initializeWithTimeout: async (timeoutMs = 10000) => {
    return new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('Auth initialization timed out, setting loading to false')
        set({ loading: false })
        resolve()
      }, timeoutMs)
      
      // Check if already not loading
      if (!get().loading) {
        clearTimeout(timeout)
        resolve()
        return
      }
      
      // Poll for loading state change
      const checkLoading = () => {
        if (!get().loading) {
          clearTimeout(timeout)
          resolve()
        } else {
          setTimeout(checkLoading, 100)
        }
      }
      
      checkLoading()
    })
  },
})) 